import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * Botón "⋮" que agrupa acciones secundarias de una fila de tabla en un
 * menú desplegable, para no saturar la columna de Acciones con muchos
 * botones sueltos. El menú se abre en un portal a document.body (posición
 * fija calculada desde el botón) para que no lo recorte ningún contenedor
 * ancestro con overflow:hidden.
 *
 * items: [{ icon, label, color, onClick }] — los items falsy se ignoran,
 * así que se puede pasar condiciones inline: items={[cond && {...}]}
 */
export default function RowActionsMenu({ items }) {
  const filtrados = (items || []).filter(Boolean);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef  = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      const insideBtn  = btnRef.current?.contains(e.target);
      const insideMenu = menuRef.current?.contains(e.target);
      if (!insideBtn && !insideMenu) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    function updateRect() {
      if (btnRef.current) setRect(btnRef.current.getBoundingClientRect());
    }
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open]);

  if (filtrados.length === 0) return null;

  // Si no cabe debajo del botón, el menú se abre hacia arriba.
  let menuTop = 0;
  if (rect) {
    const margin      = 6;
    const alturaMenu  = filtrados.length * 34 + 8;
    const spaceBelow  = window.innerHeight - rect.bottom - margin;
    menuTop = spaceBelow >= alturaMenu
      ? rect.bottom + margin
      : rect.top - alturaMenu - margin;
  }

  return (
    <div ref={btnRef} style={{ display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Más acciones"
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 30, height: 30, padding: 0,
          background: open ? "var(--hover-bg-strong)" : "var(--hover-bg)",
          border: "1px solid var(--border-card)", borderRadius: 6,
          color: "var(--text-secondary)", fontSize: "0.9rem", cursor: "pointer",
          transition: "background 0.15s",
        }}
        onMouseOver={(e) => { if (!open) e.currentTarget.style.background = "var(--hover-bg-strong)"; }}
        onMouseOut={(e)  => { if (!open) e.currentTarget.style.background = "var(--hover-bg)"; }}
      >
        <i className="fa-solid fa-ellipsis-vertical" />
      </button>

      {open && rect && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed", top: menuTop, left: rect.right - 170, width: 170,
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 8, zIndex: 10050, overflow: "hidden", padding: 4,
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          }}
        >
          {filtrados.map((it, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setOpen(false); it.onClick?.(); }}
              style={{
                display: "flex", alignItems: "center", gap: 9,
                width: "100%", padding: "7px 10px", borderRadius: 6,
                background: "transparent", border: "none", textAlign: "left",
                cursor: "pointer", color: it.color || "var(--text-primary)",
                fontSize: "0.82rem", fontWeight: 500, transition: "background 0.1s",
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = "var(--hover-bg-strong)"; }}
              onMouseOut={(e)  => { e.currentTarget.style.background = "transparent"; }}
            >
              <i className={`fa-solid ${it.icon}`} style={{ width: 14, textAlign: "center" }} />
              {it.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
